import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import Icon from '../common/Icons';
import ToastStack from '../common/ToastStack';
import { useToast } from '../../hooks/useToast';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import ImportDNSView from '../admin-personnel/import-dns/ImportDNSView';
import ImportDebaucheView from '../admin-personnel/import-debauche/ImportDebaucheView';
import MemberHierarchyTable from '../admin-personnel/members/MemberHierarchyTable';
import MemberFormModal from '../admin-personnel/members/MemberFormModal';
import MemberDetailsModal from '../admin-personnel/members/MemberDetailsModal';
import MemberArchiveModal from '../admin-personnel/members/MemberArchiveModal';
import MemberSelectionTray from '../admin-personnel/members/MemberSelectionTray';
import '../admin-personnel/members/MemberSelectionTray.css';
import MemberService from '../../services/memberService';
import CompanyService from '../../services/companyService';
import {
  MEMBER_TYPE_FILTERS,
  normalizeMemberTypeFilter,
  resolveMemberListQuery,
  formatMemberListCountMeta
} from './memberListUtils';
import '../companies/CompanyListUx.css';
import '../admin-personnel/AdminPersonnelPortal.css';
import '../admin-personnel/members/MemberHierarchyTable.css';

const EMPTY_FORM = {
  matricule: '',
  company_id: '',
  nom: '',
  prenom: '',
  fonction: '',
  sexe: '',
  date_naissance: '',
  date_embauche: '',
  type: 'AGENT',
  agent_id: ''
};

/**
 * Gestion adhérents (agents & ayants-droit) — partagée admin système / admin personnel.
 */
export default function MembersManagementPage({
  copy,
  badge,
  PageHeader,
  rootClassName = 'ap-dash',
  shellClassName = 'ap-shell',
  createBtnClassName = 'ap-create-btn',
  variant = 'personnel'
}) {
  const [viewMode, setViewMode] = useState('list');
  const [members, setMembers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const outlet = useOutletContext() || {};
  const localToast = useToast();
  const useLayoutToast = typeof outlet.showToast === 'function';
  const showToast = useLayoutToast ? outlet.showToast : localToast.showToast;
  const toasts = localToast.toasts;
  const removeToast = localToast.removeToast;
  const getToastDuration = localToast.getDuration;
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 350);
  const [typeFilter, setTypeFilter] = useState('AGENT');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [memberToArchive, setMemberToArchive] = useState(null);
  const [bulkArchiveIds, setBulkArchiveIds] = useState(null);
  const [archiving, setArchiving] = useState(false);
  /** Panier : id → snapshot adhérent (persiste entre les pages). */
  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [listPage, setListPage] = useState(1);
  const [listItemsPerPage] = useState(20);
  const listOffset = (listPage - 1) * listItemsPerPage;
  const [expandedAgents, setExpandedAgents] = useState({});
  const [loadingDependents, setLoadingDependents] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);

  const isArchiveView = viewMode === 'archives';
  const normalizedTypeFilter = normalizeMemberTypeFilter(typeFilter);

  const listQuery = useMemo(
    () =>
      resolveMemberListQuery({
        isArchiveView,
        typeFilter: normalizedTypeFilter,
        searchTerm: debouncedSearchTerm
      }),
    [isArchiveView, normalizedTypeFilter, debouncedSearchTerm]
  );

  const countMeta = useMemo(
    () =>
      formatMemberListCountMeta({
        total: totalMembers,
        isHierarchical: listQuery.isHierarchical,
        isArchiveView,
        hasSearch: listQuery.hasSearch,
        typeFilter: normalizedTypeFilter
      }),
    [totalMembers, listQuery.isHierarchical, listQuery.hasSearch, isArchiveView, normalizedTypeFilter]
  );

  const loadMembers = useCallback(
    async (signal) => {
      try {
        setLoading(true);
        const response = await MemberService.getAllMembers(
          {
            limit: listItemsPerPage,
            offset: listOffset,
            company_id: companyFilter !== 'all' ? parseInt(companyFilter, 10) : undefined,
            type: listQuery.listType,
            search_term: debouncedSearchTerm || undefined,
            archived: isArchiveView ? true : undefined
          },
          { signal }
        );
        setMembers(response.data || []);
        setTotalMembers(response.pagination?.total || 0);
        setTotalPages(response.pagination?.totalPages || 0);
      } catch (error) {
        if (error?.name === 'AbortError' || signal?.aborted) return;
        console.error('Erreur lors du chargement des adhérents:', error);
        showToast('Impossible de charger les adhérents', 'danger');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [
      listItemsPerPage,
      listOffset,
      companyFilter,
      listQuery.listType,
      debouncedSearchTerm,
      isArchiveView,
      showToast
    ]
  );

  const loadCompanies = useCallback(async () => {
    try {
      const response = await CompanyService.getAllCompanies();
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des sociétés:', error);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    const controller = new AbortController();
    loadMembers(controller.signal);
    return () => controller.abort();
  }, [loadMembers]);

  useEffect(() => {
    setListPage(1);
  }, [debouncedSearchTerm, companyFilter, normalizedTypeFilter, viewMode]);

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
    setListPage(1);
    setExpandedAgents({});
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setListPage(1);
    setExpandedAgents({});
  };

  const isHierarchicalList = listQuery.isHierarchical;

  // Lignes sélectionnables : titulaires en vue active hiérarchique, sinon toutes.
  const selectableMembers = isHierarchicalList
    ? members.filter((m) => m.type === 'AGENT')
    : members;
  const selectedIds = useMemo(() => new Set(selectedMap.keys()), [selectedMap]);
  const selectedMembers = useMemo(() => Array.from(selectedMap.values()), [selectedMap]);
  const selectableIds = selectableMembers.map((m) => m.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const clearSelection = () => setSelectedMap(new Map());

  const removeFromSelection = (id) =>
    setSelectedMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

  const toggleSelect = (member) =>
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(member.id)) next.delete(member.id);
      else next.set(member.id, member);
      return next;
    });

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedMap((prev) => {
        const next = new Map(prev);
        selectableIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedMap((prev) => {
        const next = new Map(prev);
        selectableMembers.forEach((m) => next.set(m.id, m));
        return next;
      });
    }
  };

  // Réinitialise le panier quand le contexte de filtre change (pas la pagination).
  useEffect(() => {
    setSelectedMap(new Map());
  }, [viewMode, typeFilter, companyFilter, debouncedSearchTerm]);

  const handleCompanyFilterChange = (value) => {
    setCompanyFilter(value);
    setListPage(1);
    setExpandedAgents({});
  };

  const loadAgentsForCompany = useCallback(async () => {
    if (formData.type === 'AGENT' || !formData.company_id) {
      setAgents([]);
      return;
    }
    try {
      const response = await MemberService.getAllMembers({
        company_id: formData.company_id,
        type: 'AGENT',
        active_only: true,
        limit: 500,
        offset: 0
      });
      setAgents(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des agents:', error);
    }
  }, [formData.company_id, formData.type]);

  useEffect(() => {
    loadAgentsForCompany();
  }, [loadAgentsForCompany]);

  const handleImportSuccess = () => {
    setListPage(1);
    setViewMode('list');
    loadMembers();
  };

  const handleDebaucheSuccess = () => {
    setListPage(1);
    setViewMode('archives');
  };

  const handleCreate = () => {
    setEditingMember(null);
    setFormData({ ...EMPTY_FORM });
    setAgents([]);
    setShowModal(true);
  };

  const handleEdit = (memberItem) => {
    setEditingMember(memberItem);
    setFormData({
      matricule: memberItem.matricule || '',
      company_id: memberItem.company_id || '',
      nom: memberItem.nom || '',
      prenom: memberItem.prenom || '',
      fonction: memberItem.fonction || '',
      sexe: memberItem.sexe || '',
      date_naissance: memberItem.date_naissance
        ? String(memberItem.date_naissance).slice(0, 10)
        : '',
      date_embauche: memberItem.date_embauche
        ? String(memberItem.date_embauche).slice(0, 10)
        : '',
      type: memberItem.type || 'AGENT',
      agent_id: memberItem.agent_id || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.matricule || formData.matricule.trim().length < 2) {
      showToast('Le matricule est obligatoire', 'danger');
      return;
    }
    if (!formData.company_id) {
      showToast('La société est obligatoire', 'danger');
      return;
    }
    if (!formData.nom || formData.nom.trim().length < 2) {
      showToast('Le nom est obligatoire', 'danger');
      return;
    }
    if (!formData.prenom || formData.prenom.trim().length < 2) {
      showToast('Le prénom est obligatoire', 'danger');
      return;
    }
    if (!formData.type) {
      showToast("Le type d'adhérent est obligatoire", 'danger');
      return;
    }
    if (formData.type !== 'AGENT' && !formData.agent_id) {
      showToast('Un agent est obligatoire pour ce type', 'danger');
      return;
    }

    try {
      const payload = {
        ...formData,
        matricule: formData.matricule.trim(),
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        fonction: formData.fonction ? formData.fonction.trim() : null,
        sexe: formData.sexe || null,
        date_naissance: formData.date_naissance || null,
        date_embauche: formData.date_embauche || null,
        agent_id: formData.type === 'AGENT' ? null : formData.agent_id
      };

      if (editingMember) {
        await MemberService.updateMember(editingMember.id, payload);
        showToast('Adhérent mis à jour avec succès', 'success');
      } else {
        await MemberService.createMember(payload);
        showToast('Adhérent créé avec succès', 'success');
      }
      setShowModal(false);
      setListPage(1);
      await loadMembers();
    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || 'Erreur lors de la sauvegarde', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        'Supprimer définitivement cet adhérent archivé ?\n\nCette action est irréversible.'
      )
    ) {
      return;
    }
    try {
      await MemberService.deleteMember(id);
      showToast('Adhérent supprimé avec succès', 'success');
      setListPage(1);
      await loadMembers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showToast(error.message || 'Erreur lors de la suppression', 'danger');
    }
  };

  const handleToggleStatus = async (memberItem) => {
    try {
      const newStatus = !memberItem.is_active;
      await MemberService.updateMember(memberItem.id, { is_active: newStatus });
      showToast(`Adhérent ${newStatus ? 'activé' : 'désactivé'} avec succès`, 'success');
      await loadMembers();
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showToast(error.message || 'Erreur lors du changement de statut', 'danger');
    }
  };

  const handleViewDetails = (memberItem) => {
    setSelectedMember(memberItem);
    setShowDetailModal(true);
  };

  const handleArchive = (memberItem) => {
    setBulkArchiveIds(null);
    setMemberToArchive(memberItem);
    setShowArchiveModal(true);
  };

  const openBulkArchive = () => {
    if (selectedMembers.length === 0) return;
    setMemberToArchive(null);
    setBulkArchiveIds(selectedMembers.map((m) => m.id));
    setShowArchiveModal(true);
  };

  const closeArchiveModal = () => {
    if (archiving) return;
    setShowArchiveModal(false);
    setMemberToArchive(null);
    setBulkArchiveIds(null);
  };

  const confirmArchive = async (reason) => {
    const isBulk = Array.isArray(bulkArchiveIds) && bulkArchiveIds.length > 0;
    if (!isBulk && !memberToArchive) return;
    setArchiving(true);
    try {
      const response = isBulk
        ? await MemberService.archiveMembers(bulkArchiveIds, reason)
        : await MemberService.archiveMember(memberToArchive.id, reason);
      showToast(response.message || 'Archivage effectué avec succès', 'success');
      setShowArchiveModal(false);
      setMemberToArchive(null);
      setBulkArchiveIds(null);
      clearSelection();
      setExpandedAgents({});
      await loadMembers();
    } catch (error) {
      console.error("Erreur lors de l'archivage:", error);
      showToast(error.message || "Erreur lors de l'archivage", 'danger');
    } finally {
      setArchiving(false);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedMembers.length === 0) return;
    if (
      !window.confirm(
        `Réintégrer (réembaucher) ${selectedMembers.length} adhérent(s) sélectionné(s) ?`
      )
    ) {
      return;
    }
    try {
      const response = await MemberService.restoreMembers(selectedMembers.map((m) => m.id));
      showToast(response.message || 'Réintégration effectuée avec succès', 'success');
      clearSelection();
      setExpandedAgents({});
      await loadMembers();
    } catch (error) {
      console.error('Erreur lors de la réintégration en lot:', error);
      showToast(error.message || 'Erreur lors de la réintégration', 'danger');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMembers.length === 0) return;
    const count = selectedMembers.length;
    if (
      !window.confirm(
        `Supprimer définitivement ${count} adhérent(s) archivé(s) ?\n\n` +
          'Cette action est irréversible : les fiches et leur rattachement seront effacés. ' +
          "L'historique médical lié peut être conservé partiellement selon les enregistrements."
      )
    ) {
      return;
    }
    try {
      const response = await MemberService.deleteMembers(selectedMembers.map((m) => m.id));
      showToast(response.message || 'Suppression effectuée', 'success');
      clearSelection();
      setExpandedAgents({});
      await loadMembers();
    } catch (error) {
      console.error('Erreur lors de la suppression en lot:', error);
      showToast(error.message || 'Erreur lors de la suppression', 'danger');
    }
  };

  const handleRestore = async (memberItem) => {
    if (
      !window.confirm(
        `Réintégrer (réembaucher) ${memberItem.nom || ''} ${memberItem.prenom || ''} ?`.trim()
      )
    ) {
      return;
    }
    try {
      const response = await MemberService.restoreMember(memberItem.id);
      showToast(response.message || 'Adhérent réintégré avec succès', 'success');
      setExpandedAgents({});
      await loadMembers();
    } catch (error) {
      console.error('Erreur lors de la réintégration:', error);
      showToast(error.message || 'Erreur lors de la réintégration', 'danger');
    }
  };

  const toggleAgentDependents = async (agentId) => {
    if (expandedAgents[agentId]) {
      setExpandedAgents((prev) => {
        const next = { ...prev };
        delete next[agentId];
        return next;
      });
      return;
    }
    setLoadingDependents((prev) => ({ ...prev, [agentId]: true }));
    try {
      const response = await MemberService.getDependentsByAgent(agentId, {
        archived: isArchiveView ? true : undefined
      });
      setExpandedAgents((prev) => ({ ...prev, [agentId]: response.data || [] }));
    } catch (err) {
      showToast('Erreur lors du chargement des ayant-droits', 'danger');
    } finally {
      setLoadingDependents((prev) => ({ ...prev, [agentId]: false }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getCompanyName = (companyId) => {
    if (companyId === null || companyId === undefined) return '-';
    const company = companies.find((c) => Number(c.id) === Number(companyId));
    return company ? company.name : '-';
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <>
      {!useLayoutToast ? (
        <ToastStack toasts={toasts} onRemove={removeToast} getDuration={getToastDuration} />
      ) : null}

      <div className={rootClassName}>
        <PageHeader
          badge={badge}
          title={copy.PAGE_TITLE}
          description={copy.PAGE_SUBTITLE}
          actions={
            viewMode === 'list' ? (
              <button type="button" className={createBtnClassName} onClick={handleCreate}>
                <Icon name="plus" size={14} />
                <span>{copy.BTN_CREATE}</span>
              </button>
            ) : null
          }
        />

        <section
          className={`${shellClassName} ${
            viewMode === 'import' || viewMode === 'debauche' ? 'has-dns-import' : ''
          }`}
        >
          <div className="ap-tabs ap-tabs--members" role="tablist" aria-label="Modes de gestion des adhérents">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'list'}
              className={`ap-tab ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => handleViewModeChange('list')}
            >
              <Icon name="list" size={16} />
              <span>{copy.TAB_LIST}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'archives'}
              className={`ap-tab ${viewMode === 'archives' ? 'is-active' : ''}`}
              onClick={() => handleViewModeChange('archives')}
            >
              <Icon name="inbox" size={16} />
              <span>{copy.TAB_ARCHIVES || 'Archives'}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'import'}
              className={`ap-tab ${viewMode === 'import' ? 'is-active' : ''}`}
              onClick={() => handleViewModeChange('import')}
            >
              <Icon name="cloudUpload" size={16} />
              <span>{copy.TAB_IMPORT}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'debauche'}
              className={`ap-tab ${viewMode === 'debauche' ? 'is-active' : ''}`}
              onClick={() => handleViewModeChange('debauche')}
            >
              <Icon name="inbox" size={16} />
              <span>{copy.TAB_DEBAUCHE_IMPORT || 'Débauche Excel'}</span>
            </button>
          </div>

          {(viewMode === 'list' || viewMode === 'archives') && (
            <>
              <div className="co-list-filters ap-mem-filters">
                <div className="co-list-filters__row co-list-filters__row--primary">
                  <label className="co-list-search">
                    <Icon name="search" size={16} className="co-list-search__icon" aria-hidden />
                    <input
                      type="search"
                      className="co-list-search__input"
                      placeholder={copy.SEARCH_PLACEHOLDER}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      aria-label="Rechercher un adhérent"
                      autoComplete="off"
                    />
                  </label>

                  <div className="co-list-segment" role="group" aria-label="Filtrer par type">
                    {MEMBER_TYPE_FILTERS.map((filter) => (
                      <button
                        key={filter.id}
                        type="button"
                        aria-pressed={normalizedTypeFilter === filter.id}
                        className={`co-list-segment__btn ${
                          normalizedTypeFilter === filter.id ? 'is-active' : ''
                        }`}
                        onClick={() => handleTypeFilterChange(filter.id)}
                      >
                        {filter.id === 'all' ? copy.TYPE_ALL : filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="co-list-filters__row co-list-filters__row--secondary">
                  <label className="co-list-field">
                    <span className="co-list-field__label">Société</span>
                    <select
                      className="co-list-field__select"
                      value={companyFilter}
                      onChange={(e) => handleCompanyFilterChange(e.target.value)}
                      aria-label="Filtrer par société"
                    >
                      <option value="all">{copy.COMPANY_ALL}</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="co-list-results-meta" role="status" aria-live="polite">
                    <span className="co-list-results-meta__count">
                      <strong>{totalMembers}</strong>
                      <span>
                        {countMeta.countNoun}
                        {totalMembers > 1 ? 's' : ''}
                      </span>
                      {countMeta.hint ? (
                        <span className="co-list-results-meta__hint" title={countMeta.hint}>
                          {' '}
                          · {countMeta.hint}
                        </span>
                      ) : null}
                    </span>
                    {totalPages > 1 ? (
                      <span className="co-list-results-meta__total">
                        page {listPage} / {totalPages}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div
                className={
                  selectedMembers.length > 0 ? 'ap-mem-shell--has-selection' : undefined
                }
              >
              <MemberHierarchyTable
                members={members}
                hierarchical={isHierarchicalList}
                loading={loading}
                expandedAgents={expandedAgents}
                loadingDependents={loadingDependents}
                onToggleAgent={toggleAgentDependents}
                onView={handleViewDetails}
                onToggleStatus={handleToggleStatus}
                onArchive={handleArchive}
                onRestore={handleRestore}
                onDelete={handleDelete}
                getCompanyName={getCompanyName}
                formatDate={formatDate}
                isArchiveView={isArchiveView}
                selectable
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                allSelected={allSelected}
                uiVariant={variant}
              />
              </div>

              {(members.length > 0 || totalMembers > 0) && (
                <footer className="ap-mem-footer">
                  <span>
                    {totalMembers === 0
                      ? listQuery.hasSearch
                        ? 'Aucun résultat'
                        : isArchiveView
                          ? 'Aucun archivé'
                          : 'Aucun adhérent'
                      : isHierarchicalList
                        ? `Titulaires ${listOffset + 1}–${Math.min(
                            listOffset + listItemsPerPage,
                            totalMembers
                          )} sur ${totalMembers} — dépliez une ligne pour les ayants-droit`
                        : `${listQuery.hasSearch ? 'Résultats' : 'Affichage'} ${listOffset + 1}–${Math.min(
                            listOffset + listItemsPerPage,
                            totalMembers
                          )} sur ${totalMembers}`}
                  </span>
                  {totalPages > 1 && (
                    <nav className="ap-mem-page-nav" aria-label="Pagination adhérents">
                      <button
                        type="button"
                        className="ap-mem-page-btn"
                        onClick={() => setListPage(1)}
                        disabled={listPage === 1}
                        title="Première page"
                      >
                        <Icon name="chevronDoubleLeft" size={14} />
                      </button>
                      <button
                        type="button"
                        className="ap-mem-page-btn"
                        onClick={() => setListPage((p) => Math.max(1, p - 1))}
                        disabled={listPage === 1}
                        title="Page précédente"
                      >
                        <Icon name="chevronLeft" size={14} />
                      </button>
                      <span className="ap-mem-page-label">
                        {listPage} / {totalPages}
                      </span>
                      <button
                        type="button"
                        className="ap-mem-page-btn"
                        onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                        disabled={listPage >= totalPages}
                        title="Page suivante"
                      >
                        <Icon name="chevronRight" size={14} />
                      </button>
                      <button
                        type="button"
                        className="ap-mem-page-btn"
                        onClick={() => setListPage(totalPages)}
                        disabled={listPage >= totalPages}
                        title="Dernière page"
                      >
                        <Icon name="chevronDoubleRight" size={14} />
                      </button>
                    </nav>
                  )}
                </footer>
              )}
            </>
          )}

          {viewMode === 'import' && (
            <ImportDNSView
              variant={variant}
              companies={companies}
              showToast={showToast}
              onImportSuccess={handleImportSuccess}
            />
          )}

          {viewMode === 'debauche' && (
            <ImportDebaucheView
              variant={variant}
              companies={companies}
              showToast={showToast}
              onImportSuccess={handleDebaucheSuccess}
            />
          )}
        </section>
      </div>

      <MemberFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        formData={formData}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        editingMember={editingMember}
        companies={companies}
        agents={agents}
      />

      <MemberDetailsModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        member={selectedMember}
        onEdit={handleEdit}
        getCompanyName={getCompanyName}
        formatDate={formatDate}
        companies={companies}
        variant={variant}
      />

      <MemberArchiveModal
        show={showArchiveModal}
        onHide={closeArchiveModal}
        member={memberToArchive}
        bulkCount={bulkArchiveIds ? bulkArchiveIds.length : 0}
        onConfirm={confirmArchive}
        submitting={archiving}
      />

      <MemberSelectionTray
        selectedMembers={selectedMembers}
        isArchiveView={isArchiveView}
        onRemove={removeFromSelection}
        onClear={clearSelection}
        onArchive={openBulkArchive}
        onRestore={handleBulkRestore}
        onDelete={handleBulkDelete}
      />
    </>
  );
}
